'use client';

import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useDevice } from '@/app/hooks';
import { cn } from '@/app/utils';

export interface VideoHoverCardProps {
  /** 封面图 URL，作为默认显示 */
  coverUrl?: string;
  /** 视频 URL，hover 时挂载播放；缺省时仅展示封面 */
  videoUrl?: string;
  /** alt 文案（封面图） */
  alt?: string;
  /** 外层容器额外类名 */
  className?: string;
  /** 比例容器类名，默认 `aspect-video` */
  aspectClassName?: string;
  /**
   * 媒体对象的填充方式，默认 `cover`（按容器铺满，超出裁剪）。
   * 当源素材方向（如竖屏）跟容器不一致、希望完整可见时传 `contain`，
   * 此时背景由容器（黑底）补齐空隙。
   */
  objectFit?: 'cover' | 'contain';
  /** 点击回调，提供时按钮语义（button），否则只是展示用 div */
  onClick?: () => void;
  /** 叠加内容（角标 / 渐变 / 名称 overlay 等） */
  children?: ReactNode;
}

/**
 * 共享的 hover 自动播放视频卡片：
 * - 始终展示 coverUrl（无 videoUrl 时也只显示封面）
 * - 支持 hover 的设备（useDevice().supportsHover）：mouseenter 时挂上 src 并 muted/loop 播放，mouseleave 时暂停并卸载 src
 * - 加载失败：fallback 回封面，不再尝试播放
 *
 * 与 mycreations PreviewVideo 的 hover-scrub（按鼠标 X 控制 currentTime + 指示线）行为不同；
 * 本组件语义为「hover 静音循环自动播放，离开暂停回封面」。
 */
export const VideoHoverCard = ({
  coverUrl,
  videoUrl,
  alt = '',
  className,
  aspectClassName = 'aspect-video',
  objectFit = 'cover',
  onClick,
  children,
}: VideoHoverCardProps) => {
  const { supportsHover } = useDevice();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);

  const canPlayVideo = supportsHover && !!videoUrl && !hasVideoError;

  const handleMouseEnter = useCallback(() => {
    if (!canPlayVideo) return;
    const video = videoRef.current;
    if (!video) return;

    if (!video.src) {
      video.src = videoUrl as string;
    }
    setIsVideoActive(true);
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // 浏览器可能因策略阻止自动播放，静默忽略
      });
    }
  }, [canPlayVideo, videoUrl]);

  const handleMouseLeave = useCallback(() => {
    if (!canPlayVideo) return;
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    // 卸 src 并触发 load，让封面重新显示并释放资源
    video.removeAttribute('src');
    try {
      video.load();
    } catch {
      // 某些环境下 load 可能抛错，忽略即可
    }
    setIsVideoActive(false);
  }, [canPlayVideo]);

  const handleVideoError = useCallback(() => {
    setHasVideoError(true);
    setIsVideoActive(false);
  }, []);

  const containerClassName = cn(
    // 浅色模式补 light: 前缀的灰阶 token：
    // - border-system-white08 / bg-system-white04 都是白色透明，浅色模式下白底+白透明边框/底色几乎不可见。
    // - light:border-system-white12 / light:bg-system-bg03 与项目已有浅色占位卡（ChannelBindModal、FileTable 等）对齐。
    'group relative block w-full overflow-hidden rounded-12 border border-system-white08 bg-system-white04 light:border-system-white12 light:bg-system-bg03 text-left',
    onClick && 'cursor-pointer',
    aspectClassName,
    className,
  );

  const mediaFitClass = objectFit === 'contain' ? 'object-contain' : 'object-cover';

  const innerNodes = (
    <>
      {coverUrl && (
        <img
          src={coverUrl}
          alt={alt}
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full transition-opacity duration-200',
            mediaFitClass,
            isVideoActive ? 'opacity-0' : 'opacity-100',
          )}
        />
      )}
      {canPlayVideo && (
        <video
          ref={videoRef}
          poster={coverUrl}
          muted
          loop
          playsInline
          preload="none"
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full transition-opacity duration-200',
            mediaFitClass,
            isVideoActive ? 'opacity-100' : 'opacity-0',
          )}
          onError={handleVideoError}
        />
      )}
      {children}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={containerClassName}
      >
        {innerNodes}
      </button>
    );
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={containerClassName}>
      {innerNodes}
    </div>
  );
};
